import React, { useMemo, useState } from 'react';
import i18n from '../../utilities/i18n';
import { Formik, Form, FormikProps, FormikHelpers, Field } from 'formik';
import * as Yup from 'yup';
import log from '../../utilities/log';
import TextInputWithError from '../ui/forms/TextInputWithError';
import {
  Button,
  Highlight,
  HStack,
  IconBannersWarning,
  VStack,
  Text,
  IconValidationInvalid,
  ScrollView,
} from '@adc/parallax-component-library';
import SearchableDropdown from '../searchableDropdown/SearchableDropdown';
import { Dialog } from 'tamagui';
import { Site, Subject } from '../../models';
import TextAreaWithError from '../ui/forms/TextAreaWithError';
import './Modal.scss';
import { subjectIdSchema } from 'adc-sirius-module-common';
import { getErrorMessageOnEntityCreateUpdate } from '../../utilities/crudHelpers';

type CreateSubjectModalProps = {
  sites: Site[];
  trigger: React.ReactNode;
  onSuccess: (subject: Subject) => Promise<void>;
  subject?: Subject;
};

/**
 * Modal for creating a new subject.
 * @param {Site[]} sites - List of sites to select from.
 * @param {React.ReactNode} trigger - The element that triggers the modal.
 * @param {function} onSuccess - Callback function to be called when subject creation is successful
 * @param {Subject} subject - Optional subject object to pre-fill the form for editing.
 * @returns {React.JSX.Element}
 */
export default function CreateSubjectModal({
  sites,
  trigger,
  onSuccess,
  subject,
}: CreateSubjectModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const siteOptions = useMemo(() => {
    return sites.map((site) => ({
      name: site.name,
      id: site.key,
    }));
  }, [sites]);

  const getSelectedSite = (siteKey: string) => {
    const selectedSite = siteOptions.find((site) => site.id === siteKey);

    return selectedSite
      ? {
          key: selectedSite.id,
          value: selectedSite.name,
        }
      : undefined;
  };

  const initialValues: Partial<Subject> = {
    siteKey:
      subject?.siteKey || (siteOptions.length > 0 ? siteOptions[0].id : ''),
    name: subject?.name || '',
    description: subject?.description || '',
    type: subject?.type || '',
    region: subject?.region || '',
    systemNotes: [],
    userNotes: [],
  };

  const formSchema = Yup.object().shape({
    siteKey: Yup.string().required(i18n.t('Form.genericRequired')),
    name: Yup.string()
      .required(i18n.t('Form.genericRequired'))
      .matches(
        subjectIdSchema.matcher,
        i18n.t('Form.allowedSpecialCharacters', {
          characters: subjectIdSchema.allowedSpecialCharacters.join(' '),
        }),
      )
      .test(
        'len',
        () =>
          i18n.t('Form.genericMaxLength', { count: subjectIdSchema.maxLength }),
        (val) =>
          subjectIdSchema.maxLength && val
            ? val.length <= subjectIdSchema.maxLength
            : true,
      ),
    description: Yup.string().optional(),
    type: Yup.string().optional(),
    region: Yup.string().optional(),
  });

  const onSubmit = async (
    values: Partial<Subject>,
    helpers: FormikHelpers<any>,
  ) =>
    onSuccess(values as Subject)
      .then(() => {
        helpers.resetForm();
        setIsOpen(false);
        setErrorMessage(null);
      })
      .catch((err) => {
        log.error(err);

        setErrorMessage(
          getErrorMessageOnEntityCreateUpdate(err, 'Modal_CreateSubject'),
        );
      });

  return (
    <Dialog open={isOpen}>
      <Dialog.Trigger asChild onPress={() => setIsOpen(true)}>
        {trigger}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content">
          <VStack gap="$2">
            <Dialog.Title>
              <Text type="$h3.strong">
                {i18n.t('Modal_CreateSubject.title')}
              </Text>
            </Dialog.Title>
            {errorMessage ? (
              <Highlight
                variant="danger"
                iconStart={
                  <IconBannersWarning
                    color="$neutral.0"
                    accentColor="$support.danger.default"
                  />
                }
                title={i18n.t('Modal_CreateSubject.error_header')}
                body={errorMessage}
                testID="error-banner"
              />
            ) : null}
          </VStack>

          <Formik
            initialValues={initialValues}
            validationSchema={formSchema}
            onSubmit={onSubmit}
          >
            {(formikProps: FormikProps<any>) => (
              <Form noValidate className="modal-form-layout">
                <ScrollView flex={1} minHeight={0} marginVertical="$4">
                  <VStack gap="$2">
                    <TextInputWithError
                      label={i18n.t('Entity.name')}
                      name="name"
                      testID="name"
                    />
                    <TextAreaWithError
                      label={i18n.t('Entity.description')}
                      name="description"
                      testID="description"
                    />
                    <TextInputWithError
                      label={i18n.t('Entity.type')}
                      name="type"
                      testID="type"
                    />
                    <TextInputWithError
                      label={i18n.t('Entity.region')}
                      name="region"
                      testID="region"
                    />
                    <Field name="siteKey">
                      {({ field, form }: any) => (
                        <VStack
                          gap="$2"
                          onBlur={() => form.setFieldTouched(field.name, true)}
                        >
                          <SearchableDropdown
                            label={i18n.t('Entity.site')}
                            items={siteOptions.map((site) => ({
                              key: site.id,
                              value: site.name,
                            }))}
                            selectedItem={getSelectedSite(field.value)}
                            onChange={(item) =>
                              form.setFieldValue(field.name, item.key)
                            }
                            testID="site"
                          />
                          {form.touched[field.name] &&
                          form.errors[field.name] ? (
                            <HStack gap="$2">
                              <IconValidationInvalid color="$support.danger.surfaceOnColorAlt" />
                              <Text
                                type="$bodySmall.default"
                                color="$support.danger.surfaceOnColorAlt"
                                textAlign="left"
                              >
                                {form.errors[field.name]}
                              </Text>
                            </HStack>
                          ) : null}
                        </VStack>
                      )}
                    </Field>
                  </VStack>
                </ScrollView>
                <HStack justifyContent="flex-end" gap="$2" flexShrink={0}>
                  <Button
                    label={i18n.t('Common.cancel')}
                    variant="secondary"
                    disabled={formikProps.isSubmitting}
                    onPress={() => {
                      setIsOpen(false);
                      setErrorMessage(null);
                      formikProps.resetForm();
                    }}
                    testID="cancel-subject-creation"
                  />
                  <Button
                    label={i18n.t('Common.save')}
                    disabled={formikProps.isSubmitting || !formikProps.isValid}
                    onPress={formikProps.submitForm}
                    variant="primary"
                    type="submit"
                    testID="save-subject"
                  />
                </HStack>
              </Form>
            )}
          </Formik>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
